import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowUp, ArrowDown, Filter, Columns3, Download, FileDown, Save, X, ChevronLeft, ChevronRight, Layers, Check } from "lucide-react";
import { toast } from "sonner";
import { cls } from "../ui";
import { openPdfFromBlocks } from "../pagePdf";
import { ujmsGridViews, ujmsSaveGridView, ujmsDeleteGridView, ujmsSaveGridLayout } from "../../services/ujmsApi";

/** Calls onMore whenever the returned sentinel element scrolls into view (lazy row loading). */
export function useSentinel(onMore, enabled = true) {
  const ref = useRef(null);
  const cb = useRef(onMore);
  cb.current = onMore;
  useEffect(() => {
    if (!enabled || !ref.current || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver((entries) => { if (entries.some((e) => e.isIntersecting)) cb.current?.(); }, { rootMargin: "240px 0px" });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [enabled]);
  return ref;
}

const get = (row, key) => key.split(".").reduce((o, k) => (o == null ? undefined : o[k]), row);
const fmt = (v) => (v == null || v === "" ? "—" : typeof v === "object" ? JSON.stringify(v) : String(v));

/** Enterprise data grid: sort, filter, group-by (drag header), reorder, resize, hide/show, paginate, select, export, saved views. */
export function DataGrid({ id, columns, rows, total, page, pageSize, onPageChange, onSortChange, serverSide = false, loading,
  onRowClick, rowKey = "id", selectable = false, onSelectionChange, title, emptyText = "No records.", dense = true, toolbar = true, pdfTitle, compact = false,
  infinite = true, chunk = 50, onLoadMore, loadingMore = false }) {
  const storageKey = `ujms_grid_${id}`;
  const [state, setState] = useState(() => {
    try { return { ...defaultState(columns), ...(JSON.parse(localStorage.getItem(storageKey) || "{}")) }; } catch { return defaultState(columns); }
  });
  const [views, setViews] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [colsOpen, setColsOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [localPage, setLocalPage] = useState(1);
  const [shown, setShown] = useState(chunk);
  const dragCol = useRef(null);
  const resizing = useRef(null);

  useEffect(() => {
    ujmsGridViews(id).then((r) => { setViews(r.data.views || []); if (r.data.layout && !localStorage.getItem(storageKey)) setState((s) => ({ ...s, ...r.data.layout })); }).catch(() => {});
  }, [id, storageKey]);
  useEffect(() => { localStorage.setItem(storageKey, JSON.stringify(state)); }, [state, storageKey]);
  useEffect(() => { onSelectionChange?.([...selected]); }, [selected, onSelectionChange]);

  const colMap = useMemo(() => Object.fromEntries(columns.map((c) => [c.key, c])), [columns]);
  const visibleCols = state.order.filter((k) => colMap[k] && !state.hidden.includes(k)).map((k) => colMap[k]);

  const processed = useMemo(() => {
    let out = rows || [];
    Object.entries(state.filters).forEach(([k, v]) => {
      if (!v) return;
      const needle = String(v).toLowerCase();
      out = out.filter((r) => fmt(colMap[k]?.value ? colMap[k].value(r) : get(r, k)).toLowerCase().includes(needle));
    });
    if (!serverSide && state.sort.key) {
      const c = colMap[state.sort.key];
      out = [...out].sort((a, b) => {
        const av = c?.value ? c.value(a) : get(a, state.sort.key); const bv = c?.value ? c.value(b) : get(b, state.sort.key);
        if (av == null) return 1; if (bv == null) return -1;
        const cmp = typeof av === "number" && typeof bv === "number" ? av - bv : String(av).localeCompare(String(bv), undefined, { numeric: true });
        return state.sort.dir === "asc" ? cmp : -cmp;
      });
    }
    return out;
  }, [rows, state.filters, state.sort, serverSide, colMap]);

  const effPage = serverSide ? page : localPage;
  const effSize = pageSize || state.pageSize;
  const effTotal = serverSide ? total : processed.length;
  const pages = infinite ? 1 : Math.max(1, Math.ceil(effTotal / effSize));
  const lazy = infinite && !compact;
  useEffect(() => { setShown(chunk); }, [rows, state.filters, state.sort, chunk]);
  const pageRows = lazy ? (serverSide ? processed : processed.slice(0, shown)) : serverSide ? processed : processed.slice((effPage - 1) * effSize, effPage * effSize);
  const hasMore = lazy && (serverSide ? (rows || []).length < (total || 0) : shown < processed.length);
  const loadMore = () => { if (!hasMore || loading || loadingMore) return; serverSide ? onLoadMore?.() : setShown((n) => n + chunk); };
  const sentinelRef = useSentinel(loadMore, hasMore);

  const groups = useMemo(() => {
    if (!state.groupBy) return null;
    const c = colMap[state.groupBy];
    const m = new Map();
    pageRows.forEach((r) => { const k = fmt(c?.value ? c.value(r) : get(r, state.groupBy)); if (!m.has(k)) m.set(k, []); m.get(k).push(r); });
    return [...m.entries()];
  }, [pageRows, state.groupBy, colMap]);

  const toggleSort = (key) => {
    const dir = state.sort.key === key && state.sort.dir === "asc" ? "desc" : "asc";
    setState((s) => ({ ...s, sort: { key, dir } }));
    onSortChange?.(key, dir);
  };
  const goPage = (p) => { const np = Math.min(pages, Math.max(1, p)); serverSide ? onPageChange?.(np, effSize) : setLocalPage(np); };
  const setSize = (n) => { setState((s) => ({ ...s, pageSize: n })); serverSide ? onPageChange?.(1, n) : setLocalPage(1); };

  const onHeaderDragStart = (key) => (e) => { dragCol.current = key; e.dataTransfer.setData("text/plain", key); e.dataTransfer.effectAllowed = "move"; };
  const onHeaderDrop = (targetKey) => (e) => {
    e.preventDefault();
    const src = dragCol.current || e.dataTransfer.getData("text/plain");
    if (!src || src === targetKey) return;
    setState((s) => { const order = s.order.filter((k) => k !== src); order.splice(order.indexOf(targetKey), 0, src); return { ...s, order }; });
    dragCol.current = null;
  };
  const onGroupDrop = (e) => { e.preventDefault(); const key = dragCol.current || e.dataTransfer.getData("text/plain"); if (key) setState((s) => ({ ...s, groupBy: key })); dragCol.current = null; };

  const startResize = (key) => (e) => {
    e.preventDefault(); e.stopPropagation();
    resizing.current = { key, startX: e.clientX, startW: state.widths[key] || 140 };
    const move = (ev) => { const w = Math.max(60, resizing.current.startW + ev.clientX - resizing.current.startX); setState((s) => ({ ...s, widths: { ...s.widths, [key]: w } })); };
    const up = () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); resizing.current = null; };
    window.addEventListener("mousemove", move); window.addEventListener("mouseup", up);
  };

  const cellText = (r, c) => (c.text ? c.text(r) : fmt(c.value ? c.value(r) : get(r, c.key)));
  const exportCsv = () => {
    const lines = [visibleCols.map((c) => `"${c.label}"`).join(",")].concat(processed.map((r) => visibleCols.map((c) => `"${String(cellText(r, c)).replace(/"/g, '""')}"`).join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${id}.csv`; a.click();
    toast.success(`${processed.length} row(s) exported to CSV`);
  };
  const exportPdf = () => openPdfFromBlocks({ title: pdfTitle || title || id, blocks: [{ type: "table", headers: visibleCols.map((c) => c.label), rows: processed.slice(0, 1000).map((r) => visibleCols.map((c) => String(cellText(r, c)))) }] });
  const saveView = async () => {
    const name = window.prompt("Name this view");
    if (!name) return;
    try { const r = await ujmsSaveGridView({ grid: id, name, state }); setViews((v) => [...v.filter((x) => x.id !== r.data.id), r.data]); toast.success(`View "${name}" saved`); } catch (e) { toast.error(e.response?.data?.detail || "Could not save view"); }
  };
  const saveLayout = async () => { try { await ujmsSaveGridLayout({ grid: id, state }); toast.success("Column layout saved to your profile"); } catch { toast.error("Could not save layout"); } };
  const applyView = (v) => { setState((s) => ({ ...s, ...v.state })); toast.info(`View "${v.name}" applied`); };
  const removeView = async (v) => { await ujmsDeleteGridView(v.id); setViews((vs) => vs.filter((x) => x.id !== v.id)); };

  const allSelected = pageRows.length > 0 && pageRows.every((r) => selected.has(r[rowKey]));
  const toggleAll = () => setSelected((s) => { const n = new Set(s); pageRows.forEach((r) => (allSelected ? n.delete(r[rowKey]) : n.add(r[rowKey]))); return n; });
  const toggleRow = (r) => setSelected((s) => { const n = new Set(s); n.has(r[rowKey]) ? n.delete(r[rowKey]) : n.add(r[rowKey]); return n; });

  const renderRow = (r, i) => (
    <tr key={r[rowKey] ?? i} onClick={() => onRowClick?.(r)} data-testid={`${id}-row-${r[rowKey] ?? i}`}
      className={cls("border-b border-slate-200 hover:bg-ujms-soft/70", onRowClick && "cursor-pointer", selected.has(r[rowKey]) && "bg-ujms-soft", i % 2 && "bg-slate-50/50")}>
      {selectable && <td className="px-1.5 w-7 text-center"><input type="checkbox" checked={selected.has(r[rowKey])} onChange={() => toggleRow(r)} onClick={(e) => e.stopPropagation()} data-testid={`${id}-select-${r[rowKey]}`} /></td>}
      {visibleCols.map((c) => (
        <td key={c.key} className={cls("px-1.5 border-r border-slate-100 last:border-r-0 align-top", compact ? "py-px text-[11px] leading-4" : dense ? "py-[2px] text-[12px]" : "py-1.5 text-sm", c.className)} style={{ width: state.widths[c.key] }}>
          {c.render ? c.render(r) : cellText(r, c)}
        </td>
      ))}
    </tr>
  );

  return (
    <div className={cls("bg-white flex flex-col", !compact && "border border-slate-300")} data-testid={`${id}-grid`}>
      {toolbar && !compact && (
        <div className="flex items-center gap-1.5 px-2 py-1 border-b border-slate-200 bg-slate-50 text-[11px] flex-wrap">
          {title && <span className="font-semibold text-slate-700 mr-2">{title}</span>}
          <span className="text-slate-500">{effTotal.toLocaleString()} record{effTotal === 1 ? "" : "s"}{selected.size ? ` · ${selected.size} selected` : ""}</span>
          <div className="ml-auto flex items-center gap-1">
            <GBtn onClick={() => setFiltersOpen((v) => !v)} active={filtersOpen || Object.values(state.filters).some(Boolean)} icon={Filter} label="Filter" testId={`${id}-filter-btn`} />
            <GBtn onClick={() => setColsOpen((v) => !v)} active={colsOpen} icon={Columns3} label="Columns" testId={`${id}-columns-btn`} />
            <GBtn onClick={exportCsv} icon={Download} label="CSV" testId={`${id}-export-csv`} />
            <GBtn onClick={exportPdf} icon={FileDown} label="PDF" testId={`${id}-export-pdf`} />
            <GBtn onClick={saveView} icon={Save} label="Save view" testId={`${id}-save-view`} />
            <GBtn onClick={saveLayout} icon={Check} label="Save layout" testId={`${id}-save-layout`} />
            <GBtn onClick={() => setState(defaultState(columns))} icon={X} label="Reset" testId={`${id}-reset-layout`} />
            {views.length > 0 && (
              <select className="border border-slate-300 rounded-sm px-1 py-0.5 text-[11px] bg-white" defaultValue="" onChange={(e) => { const v = views.find((x) => x.id === e.target.value); if (v) applyView(v); e.target.value = ""; }} data-testid={`${id}-views-select`}>
                <option value="">Saved views…</option>
                {views.map((v) => <option key={v.id} value={v.id}>{`${v.name}${v.shared ? " (shared)" : ""}`}</option>)}
              </select>
            )}
          </div>
        </div>
      )}
      {colsOpen && (
        <div className="flex flex-wrap gap-2 px-2 py-1.5 border-b border-slate-200 bg-white text-[11px]" data-testid={`${id}-columns-panel`}>
          {state.order.filter((k) => colMap[k]).map((k) => (
            <label key={k} className="flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={!state.hidden.includes(k)} onChange={() => setState((s) => ({ ...s, hidden: s.hidden.includes(k) ? s.hidden.filter((x) => x !== k) : [...s.hidden, k] }))} data-testid={`${id}-col-toggle-${k}`} /> {colMap[k].label}</label>
          ))}
          <button className="text-ujms-dark underline ml-auto" onClick={() => setState(defaultState(columns))} data-testid={`${id}-reset-layout-panel`}>Reset layout</button>
          {views.length > 0 && views.map((v) => <button key={v.id} className="text-red-700 underline" onClick={() => removeView(v)} title={`Delete view ${v.name}`}>× {v.name}</button>)}
        </div>
      )}
      <div onDragOver={(e) => e.preventDefault()} onDrop={onGroupDrop} data-testid={`${id}-group-zone`}
        className={cls("px-2 text-[10px] border-b border-dashed border-slate-300 flex items-center gap-1.5 leading-[18px]", state.groupBy ? "bg-ujms-soft text-ujms-deep" : "bg-[#fafbfc] text-slate-400")}>
        <Layers className="w-3 h-3" />
        {state.groupBy ? <>Grouped by <b>{colMap[state.groupBy]?.label}</b> <button className="ml-1 text-slate-500 hover:text-red-700" onClick={() => setState((s) => ({ ...s, groupBy: null }))} data-testid={`${id}-ungroup`}><X className="w-3 h-3" /></button></> : "Drag a column header and drop it here to group by that column"}
      </div>
      <div className="overflow-auto">
        <table className="w-full border-collapse table-fixed" style={{ minWidth: visibleCols.reduce((a, c) => a + (state.widths[c.key] || 140), 0) }}>
          <thead>
            <tr className="tyler-th select-none">
              {selectable && <th className="w-7 px-1.5"><input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid={`${id}-select-all`} /></th>}
              {visibleCols.map((c) => (
                <th key={c.key} draggable onDragStart={onHeaderDragStart(c.key)} onDragOver={(e) => e.preventDefault()} onDrop={onHeaderDrop(c.key)}
                  onClick={() => c.sortable !== false && toggleSort(c.key)} style={{ width: state.widths[c.key] || 140 }}
                  className={cls("relative text-left px-1.5 text-[11px] font-semibold border-r border-white/25 cursor-pointer whitespace-nowrap", compact ? "py-px leading-[18px]" : "py-[3px]")} data-testid={`${id}-th-${c.key}`}>
                  <span className="flex items-center gap-1 pr-3"><span className="truncate">{c.label}</span>
                    {state.sort.key === c.key && (state.sort.dir === "asc" ? <ArrowUp className="w-3 h-3 shrink-0" /> : <ArrowDown className="w-3 h-3 shrink-0" />)}
                    <button className={cls("ml-auto shrink-0 rounded-sm p-px", state.filters[c.key] ? "bg-white/30" : "opacity-70 hover:opacity-100 hover:bg-white/20")} title="Filter column" onClick={(e) => { e.stopPropagation(); setFiltersOpen((v) => !v); }} data-testid={`${id}-funnel-${c.key}`}><Filter className="w-2.5 h-2.5" /></button>
                  </span>
                  <span onMouseDown={startResize(c.key)} className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-white/40" />
                </th>
              ))}
            </tr>
            {filtersOpen && (
              <tr className="bg-slate-50 border-b border-slate-200">
                {selectable && <th />}
                {visibleCols.map((c) => (
                  <th key={c.key} className="px-1 py-0.5">
                    <input className="w-full border border-slate-300 rounded-sm px-1 py-0.5 text-[11px] font-normal" placeholder="filter…" value={state.filters[c.key] || ""}
                      onChange={(e) => setState((s) => ({ ...s, filters: { ...s.filters, [c.key]: e.target.value } }))} data-testid={`${id}-filter-${c.key}`} />
                  </th>
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {loading && <tr><td colSpan={visibleCols.length + (selectable ? 1 : 0)} className="text-center text-xs text-slate-400 py-4">Loading…</td></tr>}
            {!loading && pageRows.length === 0 && <tr><td colSpan={visibleCols.length + (selectable ? 1 : 0)} className="text-center text-xs text-slate-400 py-4" data-testid={`${id}-empty`}>{emptyText}</td></tr>}
            {!loading && !groups && pageRows.map(renderRow)}
            {!loading && groups && groups.map(([g, rs]) => (
              <React.Fragment key={g}>
                <tr className="bg-ujms-tint/60 border-b border-slate-300"><td colSpan={visibleCols.length + (selectable ? 1 : 0)} className="px-2 py-1 text-[11px] font-bold text-ujms-deep" data-testid={`${id}-group-${g}`}>{colMap[state.groupBy]?.label}: {g} <span className="font-normal text-slate-500">({rs.length})</span></td></tr>
                {rs.map(renderRow)}
              </React.Fragment>
            ))}
            {!loading && (hasMore || loadingMore) && (
              <tr ref={sentinelRef} data-testid={`${id}-sentinel`}><td colSpan={visibleCols.length + (selectable ? 1 : 0)} className="text-center text-[11px] text-slate-400 py-1">
                {loadingMore ? "Loading more…" : <button className="underline hover:text-slate-700" onClick={loadMore} data-testid={`${id}-load-more`}>Load more ({(effTotal - pageRows.length).toLocaleString()} remaining)</button>}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      {lazy && !compact && <div className="flex items-center gap-2 px-2 py-px border-t border-slate-200 bg-[#fafbfc] text-[11px]" data-testid={`${id}-pager`}>
        <span data-testid={`${id}-shown`}>Showing <b>{pageRows.length.toLocaleString()}</b> of {effTotal.toLocaleString()}</span>
        {hasMore && <span className="text-slate-400">· rows load as you scroll</span>}
        <span className="ml-auto text-slate-400">{effTotal.toLocaleString()} total</span>
      </div>}
      {!lazy && (!compact || pages > 1) && <div className="flex items-center gap-2 px-2 py-px border-t border-slate-200 bg-[#fafbfc] text-[11px]" data-testid={`${id}-pager`}>
        <button className="p-0.5 disabled:opacity-30" onClick={() => goPage(effPage - 1)} disabled={effPage <= 1} data-testid={`${id}-prev`}><ChevronLeft className="w-4 h-4" /></button>
        <span>Page <b>{effPage}</b> of {pages}</span>
        <button className="p-0.5 disabled:opacity-30" onClick={() => goPage(effPage + 1)} disabled={effPage >= pages} data-testid={`${id}-next`}><ChevronRight className="w-4 h-4" /></button>
        <select className="border border-slate-300 rounded-sm px-1 py-0.5 bg-white ml-2" value={effSize} onChange={(e) => setSize(Number(e.target.value))} data-testid={`${id}-page-size`}>
          {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{`${n} / page`}</option>)}
        </select>
        <span className="ml-auto text-slate-400">{effTotal.toLocaleString()} total</span>
      </div>}
    </div>
  );
}

function defaultState(columns) {
  return { order: columns.map((c) => c.key), hidden: columns.filter((c) => c.hidden).map((c) => c.key), widths: Object.fromEntries(columns.filter((c) => c.width).map((c) => [c.key, c.width])),
    sort: { key: null, dir: "asc" }, filters: {}, groupBy: null, pageSize: 25 };
}

const GBtn = ({ onClick, icon: Icon, label, active, testId }) => (
  <button onClick={onClick} data-testid={testId} className={cls("inline-flex items-center gap-1 px-1.5 py-0.5 border rounded-sm hover:bg-white", active ? "border-ujms text-ujms-deep bg-ujms-soft" : "border-slate-300 text-slate-600")}>
    <Icon className="w-3 h-3" /> {label}
  </button>
);
